import template from './countdown.tpl.html';
import './countdown.scss';

/**
 * Controller for the Countdown component.
 * Displays a countdown to a specified date/time, handling multiple dates and timezones.
 */
class CountdownController {

  // Ensure all dependencies are correctly injected for minification
  static $inject = ["$rootScope", "$window", "$interval", "PageService" ,"DayjsService"];

  /** @ngInject */
  constructor($rootScope, $window, $interval, PageService, DayjsService) {

    Object.assign(this, {
      $rootScope, $window, $interval, PageService, DayjsService
    });

    // Initialize internal state properties
    this.page = undefined; // Reference to the global page object
    this.clock = { total: 0, days: "00", hours: "00", minutes: "00", seconds: "00" };
    this.targetDatetimes = []; // Array to store parsed datetime strings
    this.nextTargetDate = '';
    this.deadlineTimestamp = 0;
    this.intervalId = null; // Initialize interval ID
  }

  /**
   * Lifecycle hook: Called after the controller's bindings are initialized.
   */
  async $onInit() {
    // Fetch the page object from PageService
    this.page = await this.PageService.getGlobalPage();

    if (!this.page) {
      console.warn('CountdownController: Page object not available from PageService. Some features may not work.');
      // You might want to handle this case (e.g., disable modal features)
      return;
    }

    // Lazy load Day.js duration plugin before starting countdown
    await this.DayjsService.loadDurationPlugin();

    const webinarOpts = this.page?.options?.webinar || {};

    // Determine the source of datetime strings: component binding or page options
    let rawDatetimes = [];
    if (webinarOpts.datetime) {
      rawDatetimes = webinarOpts.datetime.split(',');
    } else if (this.datetime) { // Use component binding if present
      rawDatetimes = this.datetime.split(',');
    }

    // Trim whitespace from each datetime string
    this.targetDatetimes = rawDatetimes.map(dt => dt.trim());

    // Start the countdown if valid datetimes are provided
    if (this.targetDatetimes.length > 0) {
      this.startCountdown();
    } else {
      console.warn("CountdownController: No valid datetime(s) provided for countdown.");
    }
  }

  /**
   * Starts or restarts the countdown timer.
   */
  startCountdown() {
    // Find the next upcoming date from the list of target datetimes
    this.nextTargetDate = this._findNextUpcomingDate(this.targetDatetimes);

    if (!this.nextTargetDate) {
      console.warn("CountdownController: No future date found to set as deadline.");
      this.stopInterval(); // Ensure any previous interval is stopped
      this.clock = { total: 0, days: "00", hours: "00", minutes: "00", seconds: "00" }; // Reset clock
      return;
    }

    // Calculate the deadline timestamp (UTC milliseconds)
    this.deadlineTimestamp = this.DayjsService.parse(this.nextTargetDate).utc().valueOf();

    // Calculate initial remaining time
    const nowTimestamp = this.DayjsService.parse(this.DayjsService.now()).utc().valueOf();
    this.clock.total = this.deadlineTimestamp - nowTimestamp;

    // If there's time remaining, start the interval
    if (this.clock.total > 0) {
      this._updateClockDisplay(); // Initial display update

      // Stop any existing interval before starting a new one
      this.stopInterval();

      this.intervalId = this.$interval(() => {
        const currentNowTimestamp = this.DayjsService.parse(this.DayjsService.now()).utc().valueOf();
        const remainingTotal = this.deadlineTimestamp - currentNowTimestamp;

        if (remainingTotal > 0) {
          this.clock.total = remainingTotal;
          this._updateClockDisplay(); // Update display on each tick
        } else {
          // Countdown finished
          this.clock = { total: 0, days: "00", hours: "00", minutes: "00", seconds: "00" };
          this.stopInterval(); // Stop the interval immediately

          // Emit event if 'storage' binding is present
          if (this.storage !== undefined) {
            this.$rootScope.$emit('countdownEnd', true);
          }

          // Reload the page
          this.$window.location.reload();
        }
      }, 1000); // Update every second
    } else {
      // Deadline is in the past or now, immediately reload
      this.$window.location.reload();
    }
  }

  /**
   * Stops the countdown interval if it's running.
   */
  stopInterval() {
    if (this.intervalId) {
      this.$interval.cancel(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Calculates and updates the clock display properties (days, hours, minutes, seconds).
   * Uses Day.js duration plugin for cleaner calculation.
   */
  _updateClockDisplay() {
    // Use dayjs.duration for cleaner time remaining calculation
    const durationObj = this.DayjsService.duration(this.clock.total);

    this.clock.days = String(Math.floor(durationObj.asDays())).padStart(2, '0');
    this.clock.hours = String(durationObj.hours()).padStart(2, '0');
    this.clock.minutes = String(durationObj.minutes()).padStart(2, '0');
    this.clock.seconds = String(durationObj.seconds()).padStart(2, '0');
  }

  /**
   * Finds the next upcoming date from a list of datetime strings.
   * @param {string[]} datetimes - Array of datetime strings.
   * @returns {string|null} The next upcoming date string, or null if no future date is found.
   */
  _findNextUpcomingDate(datetimes) {
    const now = this.DayjsService.parse(this.DayjsService.now()).utc().valueOf(); // Current time in UTC for comparison
    let nextDate = null;
    let minDiff = Infinity;

    for (const dt of datetimes) {
      const targetTimestamp = this.DayjsService.parse(dt).utc().valueOf();
      const diff = targetTimestamp - now;

      // If this date is in the future and is closer than any previously found future date
      if (diff > 0 && diff < minDiff) {
        minDiff = diff;
        nextDate = dt;
      }
    }
    return nextDate;
  }

  /**
   * Lifecycle hook: Called when the component's containing scope is destroyed.
   * Ensures the countdown interval is cleared to prevent memory leaks.
   */
  $onDestroy() {
    this.stopInterval();
  }
}

class CountdownComponent {
  constructor() {
    this.bindings = {
      datetime: '@?',
      storage: '@?'
    };
    this.template = template;
    this.controller = CountdownController;
  }
}

export default angular
  .module('countdown.component', [])
  .component('countdown', new CountdownComponent())
  .name;